import { useEffect, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { motion } from 'framer-motion';

const navButtonClassName =
  'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[4px] bg-[rgb(var(--ala-border))]/60 text-[rgb(var(--ala-text))] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] transition hover:bg-[rgb(var(--ala-border))]';

export function HomeTestimonials() {
  const [index, setIndex] = useState(0);
  const total = 4;

  const prev = () => setIndex((current) => (current <= 0 ? total - 1 : current - 1));
  const next = () => setIndex((current) => (current + 1) % total);

  useEffect(() => {
    const id = window.setInterval(() => {
      setIndex((current) => (current + 1) % total);
    }, 7000);
    return () => window.clearInterval(id);
  }, [total]);

  return (
    <section className="overflow-hidden bg-[rgb(var(--ala-surface))] px-4 pb-20 pt-8 md:px-8 md:py-32">
      <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-10">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
            <RichTextContent
              path="hometestimonials.eyebrow"
              label="Eyebrow"
              as="span"
              className="font-heading text-sm font-medium uppercase tracking-[-0.3px] text-[rgb(var(--ala-text-muted))]"
            />
          </div>
          <RichTextContent
            path="hometestimonials.headline"
            label="Headline"
            as="h2"
            className="font-heading text-[clamp(2rem,8vw,60px)] font-medium leading-[1.1] tracking-[-0.04em] text-[rgb(var(--ala-text))]"
          />
        </div>

        <div className="h-px w-full bg-[rgb(var(--ala-accent))]" aria-hidden />

        <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between md:gap-12">
          <div className="relative min-h-[12rem] min-w-0 flex-1">
            <motion.blockquote
              initial={false}
              animate={{ opacity: index === 0 ? 1 : 0, x: index === 0 ? 0 : -24 }}
              transition={{ duration: 0.45, ease: 'easeInOut' }}
              className={index === 0 ? 'relative' : 'pointer-events-none absolute inset-0'}
              aria-hidden={index !== 0}
            >
              <RichTextContent
                path="hometestimonials.quote_1"
                label="Quote 1"
                as="p"
                className="font-body text-lg leading-relaxed text-[rgb(var(--ala-text))]/85 md:text-2xl md:leading-relaxed"
              />
            </motion.blockquote>
            <motion.blockquote
              initial={false}
              animate={{ opacity: index === 1 ? 1 : 0, x: index === 1 ? 0 : index < 1 ? -24 : 24 }}
              transition={{ duration: 0.45, ease: 'easeInOut' }}
              className={index === 1 ? 'relative' : 'pointer-events-none absolute inset-0'}
              aria-hidden={index !== 1}
            >
              <RichTextContent
                path="hometestimonials.quote_2"
                label="Quote 2"
                as="p"
                className="font-body text-lg leading-relaxed text-[rgb(var(--ala-text))]/85 md:text-2xl md:leading-relaxed"
              />
            </motion.blockquote>
            <motion.blockquote
              initial={false}
              animate={{ opacity: index === 2 ? 1 : 0, x: index === 2 ? 0 : index < 2 ? -24 : 24 }}
              transition={{ duration: 0.45, ease: 'easeInOut' }}
              className={index === 2 ? 'relative' : 'pointer-events-none absolute inset-0'}
              aria-hidden={index !== 2}
            >
              <RichTextContent
                path="hometestimonials.quote_3"
                label="Quote 3"
                as="p"
                className="font-body text-lg leading-relaxed text-[rgb(var(--ala-text))]/85 md:text-2xl md:leading-relaxed"
              />
            </motion.blockquote>
            <motion.blockquote
              initial={false}
              animate={{ opacity: index === 3 ? 1 : 0, x: index === 3 ? 0 : 24 }}
              transition={{ duration: 0.45, ease: 'easeInOut' }}
              className={index === 3 ? 'relative' : 'pointer-events-none absolute inset-0'}
              aria-hidden={index !== 3}
            >
              <RichTextContent
                path="hometestimonials.quote_4"
                label="Quote 4"
                as="p"
                className="font-body text-lg leading-relaxed text-[rgb(var(--ala-text))]/85 md:text-2xl md:leading-relaxed"
              />
            </motion.blockquote>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous testimonial"
              className={navButtonClassName}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
                <path d="M15 6l-6 6 6 6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next testimonial"
              className={navButtonClassName}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeTestimonials',
  displayName: 'Home Testimonials',
  tokens: {
    'hometestimonials.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>testimonials</p>',
    },
    'hometestimonials.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue: '<p>Hear from our residents</p>',
    },
    'hometestimonials.quote_1': {
      label: 'Quote 1',
      type: 'richtext',
      defaultValue:
        '<p>Amazing apartment complex! The leasing office is very responsible and always helpful with residents. Everything is clean and well maintained, and the security is great. The office team truly cares about the community and goes above and beyond to help. One of the best properties I\'ve experienced. Highly recommend!</p>',
    },
    'hometestimonials.quote_2': {
      label: 'Quote 2',
      type: 'richtext',
      defaultValue:
        '<p>Living here has been a wonderful experience. The maintenance team responds quickly, the amenities are always spotless, and the staff genuinely cares about making this a great place to call home. I couldn\'t be happier.</p>',
    },
    'hometestimonials.quote_3': {
      label: 'Quote 3',
      type: 'richtext',
      defaultValue:
        '<p>From the tour to move-in, everything was seamless. The community is beautiful, quiet, and well-managed. Anytime I\'ve had a question, the office team has been friendly and fast to help. Highly recommend American Landmark!</p>',
    },
    'hometestimonials.quote_4': {
      label: 'Quote 4',
      type: 'richtext',
      defaultValue:
        '<p>I\'ve rented for over ten years and this is by far the best managed property I\'ve lived in. The grounds are immaculate, neighbors are respectful, and management is professional and responsive. Truly a five-star experience.</p>',
    },
  },
};

(HomeTestimonials as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default HomeTestimonials;
